// 作業中データの共有（本城さん要望 2026-08-06）API クライアント
//
// 事務所の全員が「同じ作業中の画面」を見られるようにするため、作業中データをサーバ（KV）に置く。
// 本体は数MBになるため、送受信は gzip する（CompressionStream 非対応ブラウザでは素のJSONで動く）。

import { noteAuthRequired } from "./authSignal";

export type WorkMeta = {
  rev: number;
  savedAt: string;
  savedBy: string;
  month: string;
  count: number;
  encoding: "gzip" | "none";
  bytes: number;
  // 保存ごとに変わる識別子。rev が同じでもこれが変わっていれば「自分の保存が他の人に
  // 上書きされた」＝同時保存の後勝ちが起きたと分かる（KV に条件付き書込が無いため）。
  token: string;
};

type CompressionStreamCtor = new (format: "gzip") => TransformStream<Uint8Array, Uint8Array>;

function compressionAvailable(): boolean {
  return typeof (globalThis as { CompressionStream?: unknown }).CompressionStream !== "undefined"
    && typeof (globalThis as { DecompressionStream?: unknown }).DecompressionStream !== "undefined";
}

async function gzip(json: string): Promise<{ body: BodyInit; encoding: "gzip" | "none" }> {
  if (!compressionAvailable()) return { body: json, encoding: "none" };
  try {
    const Ctor = (globalThis as unknown as { CompressionStream: CompressionStreamCtor }).CompressionStream;
    const stream = new Blob([json]).stream().pipeThrough(new Ctor("gzip"));
    const buf = await new Response(stream).arrayBuffer();
    return { body: buf, encoding: "gzip" };
  } catch {
    return { body: json, encoding: "none" };
  }
}

async function gunzip(res: Response, encoding: string): Promise<string> {
  if (encoding !== "gzip" || !res.body || !compressionAvailable()) return res.text();
  const Ctor = (globalThis as unknown as { DecompressionStream: CompressionStreamCtor }).DecompressionStream;
  const stream = res.body.pipeThrough(new Ctor("gzip"));
  return new Response(stream).text();
}

// 共有データの「版」だけを確認する（ポーリング用・軽い）
//
// reachable は「APIから正しい返事が返ったか」。通信エラー・500・非JSON応答（ローカル開発の
// vite dev など）では false。呼び出し側はこれで「共有が無い環境」と「一時的な障害」を区別する
// ＝一度の通信エラーで共有機能を丸ごと無効にしてしまわないため。
export async function fetchWorkMeta(): Promise<{
  ok: boolean;
  reachable: boolean;
  configured: boolean;
  meta: WorkMeta | null;
  error?: string;
}> {
  try {
    const r = await fetch("/api/work/meta", { cache: "no-store" });
    // ログイン切れ（401）は「共有機能が無い環境」ではない。configured:false にすると initSync が
    // 共有を恒久的にオフにしてしまう（2026-09-07 の穴）。AuthGate に知らせて再ログインへ回す
    if (noteAuthRequired(r)) return { ok: false, reachable: true, configured: true, meta: null, error: "ログインが必要です" };
    const ct = r.headers.get("content-type") || "";
    if (!ct.includes("application/json")) {
      return { ok: false, reachable: false, configured: false, meta: null, error: "API未提供（ローカル環境）" };
    }
    const data = (await r.json()) as { ok: boolean; configured?: boolean; meta?: WorkMeta | null; error?: string };
    // 200 でも ok:false（サーバ内部エラー）はあり得る。その場合 reachable は true だが ok は false
    return { ok: data.ok, reachable: true, configured: !!data.configured, meta: data.meta ?? null, error: data.error };
  } catch (e) {
    return { ok: false, reachable: false, configured: false, meta: null, error: e instanceof Error ? e.message : String(e) };
  }
}

// 共有データ本体を取得する
export async function fetchWorkDoc(): Promise<{
  ok: boolean;
  configured: boolean;
  empty?: boolean;
  json?: string;
  rev?: number;
  token?: string;
  savedAt?: string;
  savedBy?: string;
  error?: string;
}> {
  try {
    const r = await fetch("/api/work/get", { cache: "no-store" });
    if (noteAuthRequired(r)) return { ok: false, configured: true, error: "ログインが必要です" };
    const ct = r.headers.get("content-type") || "";
    if (ct.includes("application/json")) {
      const data = (await r.json()) as { ok: boolean; configured?: boolean; empty?: boolean; error?: string };
      if (data.empty) return { ok: true, configured: true, empty: true };
      return { ok: false, configured: !!data.configured, error: data.error || "共有データを取得できませんでした" };
    }
    const encoding = r.headers.get("X-Work-Encoding") || "none";
    const json = await gunzip(r, encoding);
    return {
      ok: true,
      configured: true,
      json,
      rev: Number(r.headers.get("X-Work-Rev") || "0"),
      token: r.headers.get("X-Work-Token") || "",
      savedAt: r.headers.get("X-Work-Saved-At") || undefined,
      savedBy: decodeURIComponent(r.headers.get("X-Work-Saved-By") || ""),
    };
  } catch (e) {
    return { ok: false, configured: false, error: e instanceof Error ? e.message : String(e) };
  }
}

// 共有データを保存する。baseRev がサーバの版と食い違えば conflict（＝他の人が先に保存した）。
export async function saveWorkDoc(
  json: string,
  opts: { month: string; count: number; baseRev: number | null; force?: boolean }
): Promise<{ ok: boolean; meta?: WorkMeta; reason?: "conflict" | "empty"; error?: string }> {
  try {
    const { body, encoding } = await gzip(json);
    const r = await fetch("/api/work/save", {
      method: "POST",
      headers: {
        "Content-Type": "application/octet-stream",
        "X-Work-Encoding": encoding,
        "X-Work-Base-Rev": opts.baseRev == null ? "" : String(opts.baseRev),
        "X-Work-Month": opts.month,
        "X-Work-Count": String(opts.count),
        ...(opts.force ? { "X-Work-Force": "1" } : {}),
      },
      body,
    });
    if (noteAuthRequired(r)) return { ok: false, error: "ログインが必要です" };
    const ct = r.headers.get("content-type") || "";
    if (!ct.includes("application/json")) return { ok: false, error: "API未提供（ローカル環境）" };
    const data = (await r.json()) as { ok: boolean; meta?: WorkMeta; reason?: "conflict" | "empty"; error?: string };
    return { ok: data.ok, meta: data.meta, reason: data.reason, error: data.error };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}

// 1世代前として退避されている内容（全消去・再オープン・上書きの直前に自動で取られる）
export async function fetchPrevWork(): Promise<{ ok: boolean; prev: WorkMeta | null; error?: string }> {
  try {
    const r = await fetch("/api/work/restore", { cache: "no-store" });
    if (noteAuthRequired(r)) return { ok: false, prev: null, error: "ログインが必要です" };
    const ct = r.headers.get("content-type") || "";
    if (!ct.includes("application/json")) return { ok: false, prev: null, error: "API未提供（ローカル環境）" };
    const data = (await r.json()) as { ok: boolean; prev?: WorkMeta | null; error?: string };
    return { ok: data.ok, prev: data.prev ?? null, error: data.error };
  } catch (e) {
    return { ok: false, prev: null, error: e instanceof Error ? e.message : String(e) };
  }
}

// 1世代前の内容を現在の共有データとして書き戻す
export async function restorePrevWork(): Promise<{ ok: boolean; meta?: WorkMeta; error?: string }> {
  try {
    const r = await fetch("/api/work/restore", { method: "POST" });
    if (noteAuthRequired(r)) return { ok: false, error: "ログインが必要です" };
    const ct = r.headers.get("content-type") || "";
    if (!ct.includes("application/json")) return { ok: false, error: "API未提供（ローカル環境）" };
    const data = (await r.json()) as { ok: boolean; meta?: WorkMeta; error?: string };
    return { ok: data.ok, meta: data.meta, error: data.error };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}
